import React, { useEffect, useState } from 'react';
import { Modal, Button, Row, Col, Spinner } from 'react-bootstrap';
// MAKE SURE to import postRequest along with getRequest
import { getRequest, postRequest } from '../../../utils/api';
import { toast } from 'react-toastify';
import '../Component/waba-details-modal.css';

// ---- Types matching the WhatsApp Business API payload ----------------------
interface PhoneNumber {
  display_phone_number: string;
  status: string;
  name_status: string;
  code_verification_status: string;
  quality_rating: string;
  id: string;
}

interface BusinessAccount {
  account_review_status: string;
  name: string;
  status: string;
  phone_numbers?: {
    data: PhoneNumber[];
  };
  id: string;
}

interface BusinessAccountModalProps {
  show: boolean;
  onHide: () => void;
  userID: string;
  initialData?: BusinessAccount | null; // Ye prop parent se user_meta_account_info receive karega
  onSyncComplete?: () => void;
  userData: any;
}

// ---- Status → pill variant + icon mapping ----------------------------------
type PillVariant = 'success' | 'danger' | 'warning' | 'neutral';

const STATUS_VARIANT: Record<string, PillVariant> = {
  APPROVED: 'success',
  ACTIVE: 'success',
  CONNECTED: 'success',
  VERIFIED: 'success',
  DECLINED: 'danger',
  REJECTED: 'danger',
  EXPIRED: 'danger',
  PENDING: 'warning',
  // Quality rating colors
  GREEN: 'success',
  YELLOW: 'warning',
  RED: 'danger',
  UNKNOWN: 'neutral',
};

const VARIANT_ICON: Record<PillVariant, string> = {
  success: 'ri-checkbox-circle-fill',
  danger: 'ri-close-circle-fill',
  warning: 'ri-time-fill',
  neutral: 'ri-question-fill',
};

const getVariant = (value: string): PillVariant => STATUS_VARIANT[value] || 'warning';

const StatusPill = ({ value }: { value: string }) => {
  const variant = getVariant(value);
  return (
    <span className={`waba-pill waba-pill--${variant}`}>
      <i className={VARIANT_ICON[variant]}></i>
      {value}
    </span>
  );
};

// ---- Sub-component for copying IDs -----------------------------------------
const CopyableId = ({ label, value }: { label: string; value: string }) => {
  const [copied, setCopied] = useState(false);

  const doCopy = async () => {
    if (value === 'N/A') return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      // clipboard may be unavailable; fail silently
    }
  };

  return (
    <div className="waba-id-row mt-2">
      <div className="overflow-hidden">
        <div className="text-uppercase waba-label-text">{label}</div>
        <div className="text-truncate font-monospace waba-value-text">{value}</div>
      </div>
      <button
        type="button"
        className={`waba-copy-btn ${copied ? 'waba-copy-btn--copied' : ''}`}
        onClick={doCopy}
        disabled={value === 'N/A'}
        aria-label={`Copy ${label}`}
      >
        {copied ? (
          <>
            <i className="ri-check-line"></i> Copied
          </>
        ) : (
          <>
            <i className="ri-file-copy-line"></i> Copy
          </>
        )}
      </button>
    </div>
  );
};

// ---- Main Modal Component --------------------------------------------------
const WabaDetailsModal = ({
  show,
  onHide,
  userID,
  initialData,
  onSyncComplete,
  userData
}: BusinessAccountModalProps) => {
  const [businessAccount, setBusinessAccount] = useState<BusinessAccount | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);

  useEffect(() => {
    if (show) {
      setBusinessAccount(initialData || null);
    } else {
      setIsRefreshing(false);
      setIsVerifying(false);
    }
  }, [show, initialData]);

  // Existing Sync Function
  const handleRefreshWabaStatus = async () => {
    if (!userID) return;

    try {
      setIsRefreshing(true);
      const response = await getRequest(`/user/fetchWaBaStatus?userId=${userID}`);

      if (response.status === 'success') {
        toast.success('WABA status synced successfully!');
        setBusinessAccount(response.data.businessAccount);

        if (onSyncComplete) {
          onSyncComplete();
        }
      } else {
        toast.error(response.message || 'Failed to sync');
      }
    } catch (error) {
      toast.error('Error refreshing WABA status');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Register / Complete Verification Function
  const handleRegisterNumber = async () => {
    const phoneNumberId = userData?.user_whatsapp?.phoneNumberId;
    if (!userID || !phoneNumberId || phoneNumberId === 'N/A') return;

    try {
      setIsVerifying(true);
      const response = await postRequest('/user/registerWaNumber', {
        userId: userID,
        phoneNumberId: phoneNumberId
      });

      if (response.status === 'success') {
        toast.success('Number verified and registered successfully!');
        await handleRefreshWabaStatus();
      } else {
        toast.error(response.message || 'Verification failed');
      }
    } catch (error) {
      toast.error('Error completing verification');
    } finally {
      setIsVerifying(false);
    }
  };

  // Default / Pending state logic
  const displayStatus = businessAccount?.status || 'PENDING';
  const displayReviewStatus = businessAccount?.account_review_status || 'PENDING';
  const accountId = businessAccount?.id || 'N/A';
  const accountName = businessAccount?.name || 'Business Account';

  const phone = businessAccount?.phone_numbers?.data?.[0] || {
    display_phone_number: 'N/A',
    status: 'PENDING',
    name_status: 'PENDING',
    code_verification_status: 'PENDING',
    quality_rating: 'UNKNOWN',
    id: 'N/A',
  };

  const isVerified = phone.code_verification_status === 'VERIFIED';

  return (
    <Modal show={show} onHide={onHide} centered className="waba-modal">
      <Modal.Header closeButton className="waba-modal-header">
        <div className="waba-header-brand">
          <div className="waba-header-icon">
            <i className="ri-whatsapp-fill"></i>
          </div>
          <div>
            <Modal.Title className="waba-header-title">{accountName}</Modal.Title>
            <div className="waba-header-subtitle">Business Account</div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="waba-modal-body">

        <div className="waba-phone-card mb-4">
          <Row className="g-2 mb-2">
            <Col xs={6}>
              <div className="waba-metric-tile">
                <div className="text-uppercase waba-small-label">Account Status</div>
                <StatusPill value={displayStatus} />
              </div>
            </Col>
            <Col xs={6}>
              <div className="waba-metric-tile">
                <div className="text-uppercase waba-small-label">WABA Status</div>
                <StatusPill value={displayReviewStatus} />
              </div>
            </Col>
          </Row>
          <CopyableId label="Account ID" value={accountId} />
        </div>

        <div className="waba-phone-card">
          <div className="waba-phone-header mb-3">
            <span className="waba-phone-number">{phone.display_phone_number}</span>
            <button
              type="button"
              className="waba-sync-btn bg-warning text-dark border-0"
              onClick={handleRegisterNumber}
              disabled={isRefreshing || isVerifying}
            >
              {isVerifying ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <i className="ri-shield-keyhole-line"></i>
              )}
              {isVerifying ? 'Registering...' : 'Register'}
            </button>
          </div>

          {/* Old register button commented out */}
          {/* {phone.status === 'PENDING' && phone.id !== 'N/A' && (
            <div className="mb-3 p-2 bg-warning bg-opacity-10 border border-warning rounded d-flex align-items-center justify-content-between">
              <span className="text-dark small">
                <i className="ri-information-line text-warning me-1"></i> 
                Registration pending
              </span>
              <Button 
                variant="warning" 
                size="sm" 
                className="fw-semibold px-3 py-1"
                onClick={handleRegisterNumber}
                disabled={isVerifying || isRefreshing}
              >
                {isVerifying ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  'Complete Verification'
                )}
              </Button>
            </div>
          )} */}

          <Row className="g-2 mb-2">
            <Col xs={6}>
              <div className="waba-metric-tile">
                <div className="text-uppercase waba-small-label">Phone Number</div>
                <StatusPill value={phone.status} />
              </div>
            </Col>
            {/* <Col xs={6}>
              <div className="waba-metric-tile">
                <div className="text-uppercase waba-small-label">Name Review</div>
                <StatusPill value={phone.name_status} />
              </div>
            </Col>
            <Col xs={6}>
              <div className="waba-metric-tile">
                <div className="text-uppercase waba-small-label">Code Verification</div>
                <StatusPill value={phone.code_verification_status} />
              </div>
            </Col> */}
            <Col xs={6}>
              <div className="waba-metric-tile">
                <div className="text-uppercase waba-small-label">Quality Rating</div>
                <StatusPill value={phone.quality_rating} />
              </div>
            </Col>
          </Row>

          <CopyableId label="Phone Number ID" value={phone.id} />
        </div>
      </Modal.Body>

      <Modal.Footer className="waba-modal-footer d-flex align-items-center justify-content-between">

        {/* Buttons Grouped Here */}
        <div className="d-flex gap-2">
          <button
            type="button"
            className="waba-sync-btn"
            onClick={handleRefreshWabaStatus}
            disabled={isRefreshing || isVerifying}
          >
            {isRefreshing ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <i className="ri-refresh-line"></i>
            )}
            {isRefreshing ? 'Syncing...' : 'Sync Status'}
          </button>


        </div>

        <Button className="waba-done-btn" onClick={onHide}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WabaDetailsModal;